import { useCallback } from 'react';
import { useNavigate, useParams, useLocation, type NavigateOptions } from 'react-router-dom';

/**
 * Returns a navigate function that auto-prefixes paths with /location/:locationSlug.
 * Drop-in replacement for useNavigate() within location-scoped routes.
 */
export function useLocationNavigate() {
  const navigate = useNavigate();
  const { locationSlug } = useParams<{ locationSlug: string }>();

  return useCallback(
    (path: string, options?: NavigateOptions) => {
      navigate(`/location/${locationSlug}${path}`, options);
    },
    [navigate, locationSlug]
  );
}

/**
 * Returns a full path prefixed with /location/:locationSlug.
 * Use in <Link to={locationPath('/contacts')}> or similar.
 */
export function useLocationPath() {
  const { locationSlug } = useParams<{ locationSlug: string }>();

  return useCallback(
    (path: string) => `/location/${locationSlug}${path}`,
    [locationSlug]
  );
}

/**
 * Returns the current sub-path after /location/:slug/.
 * e.g. for /location/berlin/contacts/abc → "/contacts/abc"
 */
export function useLocationSubPath(): string {
  const location = useLocation();
  const match = location.pathname.match(/^\/location\/[^/]+(\/.*)?$/);
  return match?.[1] || '/';
}
